import ValibotCommon from '@shared-schemas/common'
import OriginDestination from '@shared-types/origin-destination'
import * as v from 'valibot'
import geojson_schemas from './geojson'

export function getSelectedLinkGeometrySchema() {
	return v.pipe(
		v.union([
			geojson_schemas.feature,
			geojson_schemas.line_string,
			geojson_schemas.polygon,
			geojson_schemas.multi_polygon,
		]),
		v.transform((link) => (link.type === 'Feature' ? link.geometry : link)),
		v.transform((geometry) => {
			if (geometry?.type === 'MultiPolygon' && 'coordinates' in geometry && geometry.coordinates.length === 1) {
				return { type: 'Polygon' as const, coordinates: geometry.coordinates[0] }
			}
			return geometry
		}),
		v.union(
			[geojson_schemas.line_string, geojson_schemas.polygon],
			'Select one LineString or Polygon; multipart boundaries are not supported.',
		),
	)
}

export function getGeospatialDataGroupRecordSchema(
	params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {},
) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `user_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_display_name: v.optional(v.pipe(v.string(), v.trim()), ''),
		customer_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `customer_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		uploaded_file_id: v.nullable(
			ValibotCommon.getUuidSchema({
				expect_short_uuid,
				convert_to_full_uuid: convert_short_uuids,
				error_message: `uploaded_file_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
			}),
		),
		display_name: ValibotCommon.getNonEmptyStringSchema({ error_message: 'display_name cannot be empty' }),
		is_deleted: v.boolean(),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),

		// NOTE: not part of the table structure; created dynamically if the user_id is null
		is_default_record: v.optional(v.boolean()),

		num_zones: v.optional(v.pipe(v.number(), v.safeInteger(), v.minValue(0)), 0),
		uploaded_file: v.optional(
			v.object({
				file_format: v.nullish(v.pipe(v.string(), v.trim()), ''),
				file_size_bytes: v.nullish(v.pipe(v.number(), v.safeInteger(), v.minValue(0)), 0),
				original_filename: v.nullish(v.pipe(v.string(), v.trim()), ''),
			}),
			{
				file_format: '',
				file_size_bytes: 0,
				original_filename: '',
			},
		),
	})
}

export function getGeospatialDataRecordSchema(
	params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {},
) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		group_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `group_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		display_name: ValibotCommon.getNonEmptyStringSchema({ error_message: 'display_name cannot be empty' }),
		is_deleted: v.boolean(),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),
	})
}

export function getJobRecordSchema(params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {}) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `user_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		customer_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `customer_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		display_name: ValibotCommon.getNonEmptyStringSchema({ error_message: 'display_name cannot be empty' }),
		tomtom_job_id: v.pipe(v.number(), v.integer()),
		status: v.enum(OriginDestination.Enums.JobStatus),
		current_progress: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100))),
		is_deleted: v.boolean(),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),
	})
}

export function getUploadedFileRecordSchema(
	params: { expect_short_uuid?: boolean; convert_short_uuids?: boolean } = {},
) {
	const { expect_short_uuid = false, convert_short_uuids = false } = params

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		customer_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `customer_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		user_id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid: convert_short_uuids,
			error_message: `user_id is not a valid ${expect_short_uuid ? 'short UUID' : 'UUID'}`,
		}),
		s3_bucket: ValibotCommon.getNonEmptyStringSchema({ error_message: 's3_bucket cannot be empty' }),
		original_filename: ValibotCommon.getNonEmptyStringSchema({ error_message: 'original_filename cannot be empty' }),
		file_format: v.enum(OriginDestination.Enums.UploadedFileFormat),
		file_size_bytes: v.nullish(v.pipe(v.number(), v.safeInteger(), v.minValue(0))),
		upload_status: v.enum(OriginDestination.Enums.UploadedFileUploadStatus),
		validation_status: v.enum(OriginDestination.Enums.UploadedFileValidationStatus),
		validation_error_message: v.nullish(v.string()),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),
	})
}

interface JobSchemaOpts {
	expect_short_uuid?: boolean
	convert_to_full_uuid?: boolean
}

export function getODJobParentTypeSchema(params: { error_message_base?: string; allow_empty_value?: boolean } = {}) {
	const { error_message_base = 'parent_type field', allow_empty_value = false } = params
	const valid_parent_types = ['REGION', 'NETWORK']

	if (allow_empty_value) {
		valid_parent_types.push('')
	}

	return v.pipe(
		v.string(),
		v.toUpperCase(),
		v.picklist(valid_parent_types, `${error_message_base} must be one of ${valid_parent_types.join(', ')}`),
	)
}

export function getDayOfWeekSchema(params: { field_name?: string } = {}) {
	const { field_name = 'value' } = params
	const valid_days_of_week = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']

	return v.pipe(
		v.string(),
		v.toUpperCase(),
		v.picklist(valid_days_of_week, `${field_name} may only contain the values: ${valid_days_of_week.join(', ')}`),
	)
}

const base_job_settings_schema = {
	job_name: ValibotCommon.getNonEmptyStringSchema({
		error_message: 'job response "job_name" field cannot be empty',
	}),
	data_sources: ValibotCommon.getNonEmptyStringSchema({
		error_message: 'job response "data_sources" field cannot be empty',
	}),
	time: v.object({
		zone_id: ValibotCommon.getNonEmptyStringSchema({
			error_message: 'job response "zone_id" field cannot be empty',
		}),
		days_of_week: v.array(getDayOfWeekSchema({ field_name: 'days_of_week' })),
		date_ranges: v.pipe(
			v.array(
				v.object({
					date_range_index: v.pipe(v.number(), v.integer(), v.minValue(0)),
					start_date: v.pipe(v.string(), v.isoDate()),
					end_date: v.pipe(v.string(), v.isoDate()),
					exclusions: v.array(v.pipe(v.string(), v.isoDate())),
					name: v.union([
						v.null(),
						ValibotCommon.getNonEmptyStringSchema({
							error_message: 'job response date range "name" field cannot be an empty string',
						}),
					]),
				}),
			),
			v.minLength(1),
		),
		time_ranges: v.pipe(
			v.array(
				v.object({
					time_range_index: v.pipe(v.number(), v.integer(), v.minValue(0)),
					start_time: v.union([v.pipe(v.string(), v.isoTime()), v.pipe(v.string(), v.isoTimeSecond())]),
					end_time: v.union([v.pipe(v.string(), v.isoTime()), v.pipe(v.string(), v.isoTimeSecond())]),
				}),
			),
			v.minLength(1),
		),
	}),
	geospatial_data_group_display_name: v.optional(v.pipe(v.string(), v.trim())),
}

function getBaseJobSchema(opts: JobSchemaOpts) {
	const { expect_short_uuid = false, convert_to_full_uuid = false } = opts

	return v.object({
		id: ValibotCommon.getUuidSchema({
			expect_short_uuid,
			convert_to_full_uuid,
			error_message: 'job response "id" field is not a valid UUID',
		}),
		parent_type: v.optional(
			getODJobParentTypeSchema({ error_message_base: 'job response "parent_type" field', allow_empty_value: true }),
		),
		parent_id: v.optional(
			v.union([
				ValibotCommon.getUuidSchema({
					expect_short_uuid,
					convert_to_full_uuid,
					error_message: 'job response "parent_id" field is not a valid UUID',
				}),
				v.literal(''),
			]),
		),
	})
}

const histogram_type_schema = v.array(
	v.object({
		date_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
		time_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
		bucket_labels: v.array(
			ValibotCommon.getNonEmptyStringSchema({
				error_message: 'histograms "bucket_labels" field cannot contain empty values',
			}),
		),
		groups: v.array(
			v.object({
				origin: ValibotCommon.getNonEmptyStringSchema({
					error_message: 'histograms group "origin" field cannot be empty',
				}),
				destination: ValibotCommon.getNonEmptyStringSchema({
					error_message: 'histograms group "destination" field cannot be empty',
				}),
				bucket_values: v.array(
					// TODO: the union accepting a string can be dropped once this is off the mock data since the DB will only return numbers
					v.pipe(v.union([v.string(), v.number()]), v.transform(Number), v.safeInteger(), v.minValue(0)),
				),
			}),
		),
	}),
)

export function getFlowMatrixJobSchema(opts: JobSchemaOpts = {}) {
	return v.object({
		...getBaseJobSchema(opts).entries,
		settings: v.object({
			...base_job_settings_schema,
			regions: v.array(geojson_schemas.feature),
		}),
		results: v.array(
			v.object({
				date_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
				time_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
				links: v.array(
					v.tuple([
						v.pipe(v.number(), v.integer(), v.minValue(0)),
						v.pipe(v.number(), v.integer(), v.minValue(0)),
						v.pipe(v.number(), v.integer(), v.minValue(0)),
					]),
				),
			}),
		),
	})
}

export function getFlowMatrixJobHistogramsSchema(opts: JobSchemaOpts = {}) {
	return v.object({
		...getBaseJobSchema(opts).entries,
		settings: v.object({
			...base_job_settings_schema,
			regions: v.array(geojson_schemas.feature),
		}),
		avg_speeds_histogram: histogram_type_schema,
		durations_histogram: histogram_type_schema,
		hours_of_day_histogram: histogram_type_schema,
		lengths_histogram: histogram_type_schema,
	})
}

export const flow_matrix_via_results_schema = v.object({
	results: v.array(
		v.tuple([v.pipe(v.number(), v.safeInteger(), v.minValue(0)), v.pipe(v.number(), v.safeInteger(), v.minValue(0))]),
	),
})

const selected_link_results_schema = v.array(
	v.object({
		date_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
		time_range: v.pipe(v.number(), v.integer(), v.minValue(0)),
		nodes: v.array(
			v.tuple([
				// link id
				v.pipe(v.number(), v.integer(), v.minValue(0)),
				// link parent id (can be null)
				v.nullable(v.pipe(v.number(), v.integer(), v.minValue(0))),
				// num trips
				v.pipe(v.number(), v.integer(), v.minValue(0)),
				// frc
				v.pipe(v.number(), v.integer(), v.minValue(0)),
				// link geometry as GeoJSON LineString array
				geojson_schemas.line,
			]),
		),
	}),
)

export function getSelectedLinkJobSchema(opts: JobSchemaOpts = {}) {
	return v.object({
		...getBaseJobSchema(opts).entries,
		settings: v.object({
			...base_job_settings_schema,
			link: v.nullable(geojson_schemas.geometry),
			buffer_radius_in_meters: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(0))),
		}),
		results_incoming: selected_link_results_schema,
		results_outgoing: selected_link_results_schema,
		results_inside: v.optional(selected_link_results_schema, []),
	})
}
